import React, {KeyboardEvent, useState} from 'react';
import classNames from 'classnames';
import {getFilmLabels} from '@pluto/get-film-labels';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import {curry} from 'ramda';
import wrap from 'wraptile';
import ErrorIcon from '@material-ui/icons/Error';
import Chip from '@material-ui/core/Chip';
import CategoriesContainer from '@pluto/metadata-categories';
import SubCategoriesContainer from '@pluto/sub-categories/SubCategoriesConnected';
import {
    KeywordItem,
    CategoryItem,
    MetadataContentType,
    contentTypeRequiredFieldsConfig,
    SubCategory,
} from './QCToolsMetadataTypes';
import {
    HandleChangeInputType,
    HandleChangeSelectType,
    OnInputChangeEvent,
} from '../../QCWorkflow/QCManager/QCManagerTypes';
import {getTextWithEmptyFieldError, handleIsRequiredLabel} from './QCMetadataUtils';
import {mapKeywords} from './QCMetadataLocalized';

const MIN_KEYWORD_LENGTH = 1;
const KEYWORD_EMPTY_ERR_TEXT = `Min length ${MIN_KEYWORD_LENGTH} symbol`;
const KEYWORD_SPECIAL_CHAR_ERR_TEXT = `Special symbols are not allowed`;
const NAME_REGEXP = new RegExp(/[$%^*\/|~\'\"\:\\<>?@#.]/, 'gi'); // eslint-disable-line no-useless-escape

interface QCMetadataLocalizedTabProps {
    title: string;
    categories: CategoryItem[];
    subCategories: CategoryItem[];
    keywords: KeywordItem[];
    editable: boolean;
    titleEditable: string[];
    onTitleChange: HandleChangeInputType;
    shortDescription: string;
    longDescription: string;
    shortDescriptionEditable: string;
    longDescriptionEditable: string;
    onShortDescriptionChange: HandleChangeInputType;
    onLongDescriptionChange: HandleChangeInputType;
    categoryEditable: string;
    onCategoryChange: HandleChangeSelectType;
    subCategoryEditable: string;
    onSubCategoryChange: HandleChangeSelectType;
    keywordsEditable: KeywordItem[];
    setEditableKeywords: (v: KeywordItem[]) => void;
    newKeyword: string;
    setNewKeyword: (v: string) => void;
    metadataContentType: MetadataContentType;
    possibleCategories: CategoryItem[] | null;
}

interface AddKeywordHandlers {
    keywordsEditable: KeywordItem[];
    setEditableKeywords: (v: KeywordItem[]) => void;
    setNewKeyword: (v: string) => void;
    newKeyword: string;
    keywordError: boolean;
    setKeywordError: (v: boolean) => void;
}

export const mapKeywordsItems = (keywordsLabels: KeywordItem[]) => {
    return keywordsLabels.map((label: KeywordItem) => {
        return (
            <MenuItem
                key={label.id}
                value={label.label}
            />
        );
    });
};

export const handleKeywordDelete = wrap((keyword: KeywordItem, setEditableKeywords: (v: KeywordItem[]) => void, keywordsEditable: KeywordItem[]) => {
    const keywords = keywordsEditable.filter((item: KeywordItem) => item.label !== keyword.label);
    setEditableKeywords(keywords);
});

export const handleAddKeyword = curry((addKeywordHandlers: AddKeywordHandlers, e: KeyboardEvent) => {
    const {
        keywordsEditable,
        setEditableKeywords,
        setNewKeyword,
        newKeyword,
        keywordError,
        setKeywordError,
    } = addKeywordHandlers;

    const keyWordLabels = keywordsEditable.map(mapKeywords);
    const keyword = newKeyword.trim();

    if (keywordError) {
        setKeywordError(false);
    }

    if (e.key === 'Enter') {
        const allowedSymbols = keyword.replace(NAME_REGEXP, '');

        if (allowedSymbols.length < MIN_KEYWORD_LENGTH) return setKeywordError(true);

        if (!keyWordLabels.includes(keyword.toLowerCase())) {
            const newKeywordObj = {
                id: Date.now(),
                label: keyword,
            };

            setEditableKeywords([...keywordsEditable, newKeywordObj]);
        }

        setNewKeyword('');
    }
});

export const onHandleNewKeywordChange = curry((setNewKeyword: (v: string) => void, e: OnInputChangeEvent) => {
    setNewKeyword(e.target.value);
});

export const renderKeywords = (keywordsEditable: KeywordItem[], setEditableKeywords: (v: KeywordItem[]) => void, newKeyword: string, setNewKeyword: (v: string) => void, keywordError: boolean, setKeywordError: (v: boolean) => void) => {
    const handleChange = onHandleNewKeywordChange(setNewKeyword);

    const addKeywordHandlers = {
        keywordsEditable,
        setEditableKeywords,
        setNewKeyword,
        newKeyword,
        keywordError,
        setKeywordError,
    };

    const errorHelperText = newKeyword.length ? KEYWORD_SPECIAL_CHAR_ERR_TEXT : KEYWORD_EMPTY_ERR_TEXT;

    return (
        <div className="qc-metadata__localized-keywords_editable_chips">
            {(keywordsEditable).map((keyword: KeywordItem) => (
                <Chip
                    key={keyword.label}
                    label={keyword.label}
                    onDelete={handleKeywordDelete(keyword, setEditableKeywords, keywordsEditable)}
                />
            ))}
            <TextField
                error={keywordError}
                placeholder="Add keyword..."
                onKeyPress={handleAddKeyword(addKeywordHandlers)}
                value={newKeyword}
                onChange={handleChange}
                helperText={keywordError && errorHelperText}
            />
        </div>
    );
};

export const getEditingModeError = curry((isEditable, value) => {
    if (isEditable && !value?.length) {
        return (
            <span className="value-error">
                Field can&apos;t be empty
            </span>
        );
    }
    return null;
});

export const getCategoriesEditingError = curry((isEditable, defaultCategoriesLabels, possibleCategoriesLabels, value) => {
    if (isEditable && !value?.length || (![value].every((el: string) => possibleCategoriesLabels?.indexOf(el) !== -1)) || !possibleCategoriesLabels) {
        return (
            <span className="value-error">
                Field can&apos;t be empty
            </span>
        );
    }

    return null;
});

export const getSubCategories = (categories: CategoryItem[] | null, category: string) => {
    return categories?.find((item: CategoryItem) => item.label === category)?.subCategories;
};

export const getCategoriesError = (categoryEditable: string, categoriesLabels: JSX.Element[] | null, defaultCategoriesLabels: string[], possibleCategoriesLabels: string[] | undefined, getEmptyFieldError: any, fieldLabel: string) => {
    if (!categoryEditable) {
        return getEmptyFieldError(categoriesLabels, fieldLabel);
    }

    if (defaultCategoriesLabels && !defaultCategoriesLabels.every((el: string) => possibleCategoriesLabels?.indexOf(el) !== -1)) {
        return (
            <>
                <div className="metadata-item__value_wrong">
                    {categoriesLabels}
                </div>
                <div className="metadata-item__value-error invalid-option">
                    <div className="metadata-item__value-error_icon">
                        <ErrorIcon />
                    </div>
                    <div>
                        Invalid option, select new
                    </div>
                </div>
            </>
        );
    }

    return categoriesLabels;
};

export const QCMetadataLocalizedTab = React.memo((props: QCMetadataLocalizedTabProps) => {
    const {
        title,
        categories,
        subCategories,
        keywords,
        editable,
        titleEditable,
        onTitleChange,
        shortDescription,
        longDescription,
        shortDescriptionEditable,
        longDescriptionEditable,
        onShortDescriptionChange,
        onLongDescriptionChange,
        categoryEditable,
        subCategoryEditable,
        onSubCategoryChange,
        onCategoryChange,
        keywordsEditable,
        setEditableKeywords,
        newKeyword,
        setNewKeyword,
        metadataContentType,
        possibleCategories,
    } = props;
    const [keywordError, setKeywordError] = useState(false);

    const categoriesLabels = getFilmLabels(categories, 'film-label');
    const subCategoriesLabels = getFilmLabels(subCategories, 'film-label');
    const keywordsLabels = getFilmLabels(keywords, 'keywords-label');

    const defaultCategoriesLabels = categories.map((categoryItem: CategoryItem) => categoryItem.label);
    const defaultSubCategoriesLabels = subCategories.map((subCategoryItem: any) => subCategoryItem.label);
    const possibleCategoriesLabels = possibleCategories?.map((possibleCategory) => possibleCategory.label);
    const possibleSubCategoriesLabels = getSubCategories(possibleCategories, editable ? categoryEditable : defaultCategoriesLabels[0])?.map((item: SubCategory) => item.label);

    const metadataContentTypeRequiredFields = contentTypeRequiredFieldsConfig[metadataContentType];
    const getEmptyFieldError = getTextWithEmptyFieldError(metadataContentTypeRequiredFields);
    const getIsRequiredLabel = handleIsRequiredLabel(metadataContentTypeRequiredFields);
    const getIsError = getEditingModeError(editable);
    const getCategoriesEditingModeError = getCategoriesEditingError(editable);

    const getTitleBlock = (isEditable: boolean) => {
        return !isEditable ? (
            <div className="qc-metadata__tab-value qc-metadata__tab-value_title">
                {getEmptyFieldError(title, 'title')}
            </div>
        ) : (
            <div
                className={classNames('qc-metadata__localized-title_editable', {
                    metadataValueError: getIsError(titleEditable),
                })}
            >
                <TextField
                    value={titleEditable}
                    onChange={onTitleChange}
                    variant="filled"
                    inputProps={{
                        maxLength: 255,
                    }}
                />
                {getIsError(titleEditable)}
            </div>
        );
    };

    const getShortDescriptionBlock = (isEditable: boolean) => {
        return !isEditable ? (
            <div className="qc-metadata__tab-value">
                {getEmptyFieldError(shortDescription, 'short-description')}
            </div>
        ) : (
            <div
                className={classNames('qc-metadata__localized-description_editable', {
                    metadataValueError: getIsError(shortDescriptionEditable),
                })}
            >
                <TextField
                    multiline
                    rowsMax={3}
                    value={shortDescriptionEditable || ''}
                    onChange={onShortDescriptionChange}
                />
                {getIsError(shortDescriptionEditable)}
            </div>
        );
    };

    const getLongDescriptionBlock = (isEditable: boolean) => {
        return !isEditable ? (
            <div className="qc-metadata__tab-value">
                {getEmptyFieldError(longDescription, 'long-description')}
            </div>
        ) : (
            <div
                className={classNames('qc-metadata__localized-description_editable', {
                    metadataValueError: getIsError(longDescriptionEditable),
                })}
            >
                <TextField
                    multiline
                    rowsMax={3}
                    value={longDescriptionEditable || ''}
                    onChange={onLongDescriptionChange}
                />
                {getIsError(longDescriptionEditable)}
            </div>
        );
    };

    const getCategoriesBlock = (isEditable: boolean) => {
        return !isEditable ? (
            <div className="qc-metadata__tab-value qc-metadata__tab-value_categories">
                {getCategoriesError(
                    categoryEditable,
                    categoriesLabels,
                    defaultCategoriesLabels,
                    possibleCategoriesLabels,
                    getEmptyFieldError,
                    'categories',
                )}
            </div>
        ) : (
            <div
                className={classNames('qc-metadata__localized-categories_editable', {
                    metadataValueError: getCategoriesEditingModeError(
                        defaultCategoriesLabels,
                        possibleCategoriesLabels,
                        categoryEditable,
                    ),
                })}
            >
                <CategoriesContainer
                    categoryLabel={categoryEditable}
                    onChange={onCategoryChange}
                />
                {getCategoriesEditingModeError(
                    defaultCategoriesLabels,
                    possibleCategoriesLabels,
                    categoryEditable,
                )}
            </div>
        );
    };

    const getSubCategoriesBlock = (isEditable: boolean) => {
        return !isEditable ? (
            <div className="qc-metadata__tab-value qc-metadata__tab-value_categories">
                {getCategoriesError(
                    subCategoryEditable,
                    subCategoriesLabels,
                    defaultSubCategoriesLabels,
                    possibleSubCategoriesLabels,
                    getEmptyFieldError,
                    'sub-categories',
                )}
            </div>
        ) : (
            <div
                className={classNames('qc-metadata__localized-categories_editable', {
                    metadataValueError: getCategoriesEditingModeError(
                        defaultSubCategoriesLabels,
                        possibleSubCategoriesLabels,
                        subCategoryEditable,
                    ),
                })}
            >
                <SubCategoriesContainer
                    categoryLabel={categoryEditable}
                    subCategoryLabel={subCategoryEditable}
                    onChange={onSubCategoryChange}
                />
                {getCategoriesEditingModeError(
                    defaultSubCategoriesLabels,
                    possibleSubCategoriesLabels,
                    subCategoryEditable,
                )}
            </div>
        );
    };

    const getKeywordsBlock = (isEditable: boolean) => {
        return !isEditable ? (
            <div className="qc-metadata__tab-value qc-metadata__tab-value_keywords">
                {getEmptyFieldError(keywordsLabels, 'keywords')}
            </div>
        ) : (
            <div className="qc-metadata__keywords-wrapper">
                <div
                    className={classNames('qc-metadata__localized-keywords_editable', {
                        metadataValueError: getIsError(keywordsEditable),
                    })}
                >
                    <FormControl>
                        {renderKeywords(
                            keywordsEditable,
                            setEditableKeywords,
                            newKeyword,
                            setNewKeyword,
                            keywordError,
                            setKeywordError,
                        )}
                    </FormControl>
                </div>
                {getIsError(keywordsEditable)}
            </div>
        );
    };

    return (
        <div className="qc-metadata__localized-tab-panel">
            <div className="qc-metadata__localized__labels-wrapper">
                <div className="qc-metadata__tab-wrapper">
                    <div className="qc-metadata__tab-label qc-metadata__tab-label_title">
                        {getIsRequiredLabel('title', 'Title')}
                    </div>
                    {getTitleBlock(editable)}
                </div>
                <div className="qc-metadata__tab-wrapper">
                    <div className="qc-metadata__tab-label qc-metadata__tab-label_summary">
                        {getIsRequiredLabel('short-description', 'Short description')}
                    </div>
                    {getShortDescriptionBlock(editable)}
                </div>
                <div className="qc-metadata__tab-wrapper">
                    <div className="qc-metadata__tab-label qc-metadata__tab-label_summary">
                        {getIsRequiredLabel('long-description', 'Long description')}
                    </div>
                    {getLongDescriptionBlock(editable)}
                </div>
                <div className="qc-metadata__tab-wrapper">
                    <div className="qc-metadata__tab-label qc-metadata__tab-label_categories">
                        {getIsRequiredLabel('categories', 'Category')}
                    </div>
                    {getCategoriesBlock(editable)}
                </div>
                <div className="qc-metadata__tab-wrapper">
                    <div className="qc-metadata__tab-label qc-metadata__tab-label_categories">
                        {getIsRequiredLabel('sub-categories', 'Sub-Category')}
                    </div>
                    {getSubCategoriesBlock(editable)}
                </div>
                <div className="qc-metadata__tab-wrapper">
                    <div className="qc-metadata__tab-label qc-metadata__tab-label_keywords">
                        {getIsRequiredLabel('keywords', 'Keywords')}
                    </div>
                    {getKeywordsBlock(editable)}
                </div>
            </div>
        </div>
    );
});

export default QCMetadataLocalizedTab;

